import { CategoryId, NumberMetadata } from '../types/angelNumber';

export interface DigitTheme {
  digit: number;
  /** Short noun used in titles, e.g. "Beginnings" */
  short: string;
  /** Lowercase phrase used mid-sentence, e.g. "new beginnings" */
  keyword: string;
  /** Word used for portal-style titles */
  portalWord: string;
  essence: string;
  love: string;
  career: string;
  spiritual: string;
  reflect: string;
  affirmation: string;
  categories: CategoryId[];
  metadata: NumberMetadata;
  searchWords: string[];
}

export const digitThemes: Record<number, DigitTheme> = {
  0: {
    digit: 0,
    short: 'Potential',
    keyword: 'infinite potential',
    portalWord: 'Reset',
    essence: 'open space, a spiritual reset and the quiet pause that comes before a new cycle',
    love: 'clearing the air so a connection can begin again from honesty rather than history',
    career: 'a blank page in your working life, where very little is fixed yet and choice is still wide open',
    spiritual: 'wholeness, and a return to the calm centre you carry with you',
    reflect: 'What would you like to begin from a genuinely clean page?',
    affirmation: 'I make space for what wants to begin.',
    categories: ['spiritual_growth', 'new_beginnings', 'inner_peace', 'reflection'],
    metadata: { energy: 'Open', theme: 'Reset', element: 'Ether' },
    searchWords: ['reset', 'potential', 'wholeness', 'oneness', 'source'],
  },
  1: {
    digit: 1,
    short: 'Beginnings',
    keyword: 'new beginnings',
    portalWord: 'Awareness',
    essence: 'fresh starts, initiative and the way attention shapes what happens next',
    love: 'a new chapter in how you love — meeting someone, or meeting a familiar person differently',
    career: 'first steps, bold ideas and the courage to start before everything is certain',
    spiritual: 'the reminder that thought is creative, and that where you look tends to grow',
    reflect: 'What were you thinking about just before you noticed this number?',
    affirmation: 'I trust the next step and remain open to new possibilities.',
    categories: ['new_beginnings', 'manifestation', 'awakening'],
    metadata: { energy: 'High', theme: 'Awareness', element: 'Air' },
    searchWords: ['new beginnings', 'manifestation', 'start', 'intention', 'leadership'],
  },
  2: {
    digit: 2,
    short: 'Trust',
    keyword: 'balance, harmony and trust',
    portalWord: 'Alignment',
    essence: 'patience, partnership and the trust that things can unfold without being forced',
    love: 'listening more closely, meeting halfway and letting a bond deepen at its own pace',
    career: 'collaboration, timing and progress that is quietly building beneath the surface',
    spiritual: 'faith in the pause — the sense that waiting can be an active, gentle choice',
    reflect: 'Where in your life could you soften your grip and let timing do some of the work?',
    affirmation: 'I move at the pace of trust.',
    categories: ['balance', 'love', 'relationships', 'inner_peace'],
    metadata: { energy: 'Gentle', theme: 'Harmony', element: 'Water' },
    searchWords: ['balance', 'harmony', 'trust', 'partnership', 'patience', 'twin flame'],
  },
  3: {
    digit: 3,
    short: 'Growth',
    keyword: 'growth, creativity and expression',
    portalWord: 'Expression',
    essence: 'creative expansion, self-expression and the pull to make something of your own',
    love: 'playfulness, warmth and saying the thing you have been holding back',
    career: 'ideas that want to be made, and work that feels more like expression than obligation',
    spiritual: 'the sense of being encouraged — as though guidance is arriving through inspiration',
    reflect: 'What idea keeps returning to you, asking to be made real?',
    affirmation: 'I let myself express what is mine to say.',
    categories: ['creativity', 'transformation', 'guidance'],
    metadata: { energy: 'Bright', theme: 'Expression', element: 'Air' },
    searchWords: ['growth', 'creativity', 'guidance', 'expression', 'joy'],
  },
  4: {
    digit: 4,
    short: 'Stability',
    keyword: 'protection, stability and grounding',
    portalWord: 'Grounding',
    essence: 'foundations, steadiness and the quiet support that holds a life together',
    love: 'safety, consistency and love that shows up in small reliable ways',
    career: 'structure, discipline and the unglamorous work that makes everything else possible',
    spiritual: 'feeling accompanied — the sense that you are not carrying it all alone',
    reflect: 'What foundation in your life deserves care and attention right now?',
    affirmation: 'I am supported, and my foundations hold.',
    categories: ['protection', 'balance', 'inner_peace'],
    metadata: { energy: 'Steady', theme: 'Grounding', element: 'Earth' },
    searchWords: ['protection', 'stability', 'grounding', 'foundation', 'safety', 'angel'],
  },
  5: {
    digit: 5,
    short: 'Change',
    keyword: 'change, transformation and freedom',
    portalWord: 'Change',
    essence: 'movement, transformation and the restlessness that arrives before a shift',
    love: 'honesty about what needs to change so the connection can breathe',
    career: 'a turning point — a role, place or rhythm that no longer fits the way it did',
    spiritual: 'trusting change as a process rather than resisting it as a threat',
    reflect: 'What change have you been quietly preparing for?',
    affirmation: 'I move with change instead of against it.',
    categories: ['transformation', 'new_beginnings', 'awakening'],
    metadata: { energy: 'Dynamic', theme: 'Change', element: 'Air' },
    searchWords: ['change', 'transformation', 'freedom', 'adventure', 'movement'],
  },
  6: {
    digit: 6,
    short: 'Harmony',
    keyword: 'harmony, care and rebalancing',
    portalWord: 'Rebalancing',
    essence: 'care, home and the gentle recalibration of where your energy is going',
    love: 'nurture, family and tending to the relationships closest to you',
    career: 'balance between effort and rest, and worry that could be replaced with perspective',
    spiritual: 'a reminder to return to what is loving and simple rather than anxious',
    reflect: 'Where is your energy going, and where would you rather it went?',
    affirmation: 'I return to what is loving and simple.',
    categories: ['inner_peace', 'love', 'balance', 'relationships'],
    metadata: { energy: 'Gentle', theme: 'Care', element: 'Water' },
    searchWords: ['harmony', 'balance', 'home', 'care', 'family', 'rebalance'],
  },
  7: {
    digit: 7,
    short: 'Wisdom',
    keyword: 'inner wisdom and spiritual growth',
    portalWord: 'Insight',
    essence: 'inner knowing, study and the moments when understanding arrives quietly',
    love: 'depth over performance, and a connection that feels genuinely known',
    career: 'mastery, curiosity and following the work that keeps your mind alive',
    spiritual: 'the sense of being on the right inner path, even without external proof',
    reflect: 'What do you already know, somewhere underneath the noise?',
    affirmation: 'I trust what I know quietly.',
    categories: ['intuition', 'spiritual_growth', 'awakening', 'inner_peace'],
    metadata: { energy: 'Quiet', theme: 'Insight', element: 'Ether' },
    searchWords: ['wisdom', 'spiritual awakening', 'intuition', 'insight', 'luck'],
  },
  8: {
    digit: 8,
    short: 'Abundance',
    keyword: 'abundance, achievement and flow',
    portalWord: 'Abundance',
    essence: 'flow, reciprocity and the balance between what you give and what you receive',
    love: 'a bond where care moves in both directions rather than mostly one',
    career: 'recognition, momentum and a more comfortable relationship with money',
    spiritual: 'the reminder that receiving is not taking, and that flow works in cycles',
    reflect: 'What are you giving generously that you find hard to accept in return?',
    affirmation: 'I can give generously and still receive.',
    categories: ['abundance', 'money', 'career', 'manifestation'],
    metadata: { energy: 'Strong', theme: 'Abundance', element: 'Earth' },
    searchWords: ['abundance', 'money', 'success', 'prosperity', 'career', 'flow'],
  },
  9: {
    digit: 9,
    short: 'Release',
    keyword: 'completion, release and transition',
    portalWord: 'Completion',
    essence: 'endings, closure and the spaciousness that appears once something is set down',
    love: 'letting go of an old pattern so love can be met with who you are now',
    career: 'a chapter finishing, and the quieter question of what your work is for',
    spiritual: 'compassion, perspective and the wisdom that comes from having lived a cycle fully',
    reflect: 'What feels finished, even if you have not said so out loud yet?',
    affirmation: 'I release what is complete with gratitude.',
    categories: ['completion', 'transformation', 'spiritual_growth'],
    metadata: { energy: 'Deep', theme: 'Completion', element: 'Water' },
    searchWords: ['completion', 'release', 'endings', 'closure', 'letting go', 'service'],
  },
};

export const theme = (d: number): DigitTheme => digitThemes[d] ?? digitThemes[1];

/** Spoken forms used as search aliases, e.g. "eleven eleven". */
export const numberWords: Record<number, string> = {
  0: 'zero',
  1: 'one',
  2: 'two',
  3: 'three',
  4: 'four',
  5: 'five',
  6: 'six',
  7: 'seven',
  8: 'eight',
  9: 'nine',
  10: 'ten',
  11: 'eleven',
  12: 'twelve',
  13: 'thirteen',
  14: 'fourteen',
  15: 'fifteen',
  16: 'sixteen',
  17: 'seventeen',
  18: 'eighteen',
  19: 'nineteen',
  20: 'twenty',
  21: 'twenty one',
  22: 'twenty two',
  23: 'twenty three',
};
